import React, { useState, useEffect, memo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { societyInfo } from '../data/mock';
import { Button } from './ui/button';
import { ChevronDown, MapPin, Users, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultHeroImage = 'https://images.unsplash.com/photo-1586114539347-827507d2f006?w=1920&h=1080&fit=crop';

const Hero = memo(() => {
  const { t, language } = useLanguage();
  const [heroImage, setHeroImage] = useState(null);
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [contentResponse, textsResponse] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/texts`)
        ]);
        
        setHeroImage(contentResponse.data?.hero_image || defaultHeroImage);
        setTexts(textsResponse.data || {});
      } catch (error) {
        console.error('Error fetching hero content:', error);
        setHeroImage(defaultHeroImage);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Preload hero image
  useEffect(() => {
    if (heroImage) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = heroImage;
    }
  }, [heroImage]);

  const getSubtitle = () => {
    if (texts.hero_subtitle) {
      return language === 'sl' ? texts.hero_subtitle.sl : texts.hero_subtitle.en;
    }
    return t.hero.subtitle;
  };

  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        {/* Placeholder while loading */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-emerald-950 to-stone-900 transition-opacity duration-700 ${
            imageLoaded ? 'opacity-0' : 'opacity-100'
          }`}
        />
        {/* Actual image */}
        {heroImage && (
          <img
            src={heroImage}
            alt="Lovska družina Rakek - Notranjska"
            className={`w-full h-full object-cover transition-opacity duration-700 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/50 to-stone-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/30 to-transparent" />
      </div>

      {/* Animated Particles - using fixed positions */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { left: '10%', top: '20%', delay: '0s', duration: '3s' },
          { left: '25%', top: '40%', delay: '0.5s', duration: '4s' },
          { left: '45%', top: '15%', delay: '1s', duration: '3.5s' },
          { left: '60%', top: '60%', delay: '1.5s', duration: '4.5s' },
          { left: '80%', top: '30%', delay: '2s', duration: '3s' },
          { left: '15%', top: '70%', delay: '2.5s', duration: '4s' },
          { left: '35%', top: '80%', delay: '0.3s', duration: '3.8s' },
          { left: '70%', top: '10%', delay: '1.2s', duration: '4.2s' },
          { left: '90%', top: '50%', delay: '0.8s', duration: '3.3s' },
          { left: '5%', top: '45%', delay: '1.8s', duration: '4.8s' },
        ].map((particle, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-emerald-400/30 rounded-full animate-pulse"
            style={{
              left: particle.left,
              top: particle.top,
              animationDelay: particle.delay,
              animationDuration: particle.duration,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden bg-white backdrop-blur-sm border-4 border-white/30 shadow-2xl">
              <img 
                src={societyInfo.logo} 
                alt="LD Rakek Logo" 
                className="w-28 h-28 object-contain"
              />
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full backdrop-blur-sm">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-emerald-400 text-sm font-medium tracking-wide">
              {language === 'sl' ? 'Od leta' : 'Since'} {societyInfo.established}
            </span>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight">
            <span className="block">{t.hero.title}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-stone-300 font-light tracking-wide">
            {getSubtitle()}
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 pt-4">
            <div className="flex items-center gap-3 text-stone-300">
              <Calendar className="w-5 h-5 text-emerald-400" />
              <span>{societyInfo.established}</span>
            </div>
            <div className="flex items-center gap-3 text-stone-300">
              <Users className="w-5 h-5 text-emerald-400" />
              <span>{societyInfo.members} {language === 'sl' ? 'članov' : 'members'}</span>
            </div>
            <div className="flex items-center gap-3 text-stone-300">
              <MapPin className="w-5 h-5 text-emerald-400" />
              <span>{societyInfo.area.toLocaleString('sl-SI')} ha</span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="pt-6">
            <Button
              onClick={scrollToAbout}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 text-lg font-medium rounded-xl shadow-lg shadow-emerald-600/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:-translate-y-1"
            >
              {t.hero.cta}
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={scrollToAbout}
          className="p-2 text-stone-400 hover:text-white transition-colors"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';

export default Hero;
